<template>
	<div class="project-page-panel-filters">
		<div class="project-page-panel-filters__top">
			<app-resizable-dropdown
				v-if="!isContactsReadonly"
				position="left"
				resizable
				:initial-width="320"
				:initial-height="398"
				:min-width="320"
				:max-width="480"
				:min-height="398"
				:max-height="600"
				:close-by-click-outside="false"
				:hide-by-parent-scroll="false"
				:mobile-title="$t('contact_create_title')"
				ref="$createEditContactModal"
				name="$createContactModal"
			>
				<template #activator>
					<app-tooltip position="bottom" :text="$t('contacts_create')">
						<template #activator>
							<app-icon
								class="project-page-panel-filters__btn"
								:style="{ marginRight: '4px' }"
								name="plus-bold"
								width="22px"
								color="#979797"
								hover-color="#499259"
								cursor="pointer"
							/>
						</template>
					</app-tooltip>
				</template>
				<create-edit-contact-dropdown
					type="create"
					@on-close="$createEditContactModal.close()"
					@exist-contact-string="showExistContacts"
				/>
			</app-resizable-dropdown>
			<app-input
				v-model="filters"
				:style="{ width: '100%' }"
				:placeholder="$t('contacts_search_placeholder')"
				show-search-button
				@input="updateFilters"
				@pressEnter="searchClients"
				@search="searchClients"
			/>
		</div>
		<div class="project-page-panel-filters__bottom">
			<app-resizable-dropdown
				ref="$tagsDropdown"
				position="left"
				:close-by-click-outside="true"
				:max-width="640"
				:max-height="436"
				:mobile-title="$t('choose_tags')"
				name="$projectPageContactsTags"
			>
				<template #activator>
					<app-tooltip :text="$t('ui_tag_filter')">
						<template #activator>
							<app-icon
								class="project-page-panel-filters__btn"
								:style="{ marginRight: '4px' }"
								name="hash"
								width="18px"
								color="#979797"
								hover-color="#499259"
								cursor="pointer"
							/>
						</template>
					</app-tooltip>
				</template>
				<app-tag-selector
					:model-value="selectedTags"
					:entity-type="TagEntityType.Person"
					mode="filter"
					allow-create
					@update:model-value="emit('update:selected-tags', $event)"
					@close="$tagsDropdown?.close()"
				/>
			</app-resizable-dropdown>
			<app-select
				v-model="selectedValue"
				:items="selectItems"
				:style="{ flex: 1, minWidth: 0 }"
				@select="updateType"
			/>
			<div
				v-if="isUnionApplicable"
				class="project-page-panel-filters__use-union"
			>
				<app-tooltip
					position="top"
					:text="$t('with_holding_included')"
					:delay="1"
					:style="{ height: '17px' }"
				>
					<template #activator>
						<app-checkbox
							:value="useUnion"
							@on-input="onUseUnionChange"
						/>
					</template>
				</app-tooltip>
			</div>
		</div>
		<div class="project-page-panel-filters__address">
			<inline-address-editor
				:id="'filter-project'"
				v-model="appliedAddress"
				:always-editing="true"
			/>
		</div>
		<div
			v-if="selectedTags.length"
			class="project-page-panel-filters__tags"
		>
			<div
				v-for="tag in selectedTags"
				class="project-page-panel-filters__tags-item"
				:key="tag.Id"
			>
				<div class="project-page-panel-filters__tags-item-name">
					{{ tag.Name }}
				</div>
				<app-icon
					class="project-page-panel-filters__tags-item-delete"
					name="close-bold"
					width="12px"
					color="#D2D2D2"
					hover-color="#4D4D4D"
					cursor="pointer"
					@click.stop="emit('drop-tag', tag)"
				/>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import {
	computed,
	nextTick,
	ref,
	watch
} from "vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import AppInput from "@/components/ui-kit/input/AppInput.vue";
import AppResizableDropdown from "@/components/ui-kit/dropdown/AppResizableDropdown.vue";
import CreateEditContactDropdown from "@/components/main/clients-panel/dropdowns/CreateEditContactDropdown.vue";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";
import AppSelect, {SelectModelValue} from "@/components/ui-kit/select/AppSelect.vue";
import AppTagSelector from "@/components/ui-kit/tag-selector/AppTagSelector.vue";
import AppCheckbox from "@/components/ui-kit/checkbox/AppCheckbox.vue";
import InlineAddressEditor from "@/components/common/address-selector/InlineAddressEditor.vue";
import { TagEntityType } from "@/models/etc/types";
import { TagWithFrequency } from "@/models/interfaces/tags/tag-models";
import { useContactsAccess } from "@/composables/useContactsAccess";
import { RequestAddressContainer } from "@/composables/useAddressRequest";

type ContactTypeOption = {
	label: string,
	type: number,
	value: number | string | null,
	id?: number | null
}

type Props = {
	searchStr: string,
	searchedContactId: ContactTypeOption | null,
	contactsTypes: ContactTypeOption[],
	selectedTags: TagWithFrequency[],
	useUnion: boolean,
	isUnionApplicable: boolean,
}

const emit = defineEmits<{
	(e: 'update:searchStr', value: string): void,
	(e: 'update-searchedContactId', value: ContactTypeOption): void,
	(e: 'update:selected-tags', value: TagWithFrequency[]): void,
	(e: 'drop-tag', tag: TagWithFrequency): void,
	(e: 'on-search', address?: RequestAddressContainer | null): void,
	(e: 'update:use-union', value: boolean): void,
}>()

const props = defineProps<Props>()
const { isContactsReadonly } = useContactsAccess()
const filters = ref(props.searchStr);
const selectedValue = ref<SelectModelValue>(props.searchedContactId?.value ?? null);

const $createEditContactModal = ref<InstanceType<typeof AppResizableDropdown> | null>(null)
const $tagsDropdown = ref<InstanceType<typeof AppResizableDropdown> | null>(null)

const appliedAddress = ref<RequestAddressContainer | null>(null)

const selectItems = computed(() => props.contactsTypes.map(item => ({
	id: item.value as string | number,
	label: item.label,
})))

const updateFilters = (newVal: string) => {
	emit('update:searchStr', newVal)
}
const updateType = (newVal: SelectModelValue) => {
	const type = props.contactsTypes.find(item => item.value === newVal)
	if (type) {
		emit('update-searchedContactId', type)
	}
}

const onUseUnionChange = (value: boolean) => {
	emit('update:use-union', value)
}

const searchClients = async () => {
	emit('on-search', appliedAddress.value)
}

const showExistContacts = (contacts: string) => {
	filters.value = contacts
	updateFilters(contacts)
}

watch(() => props.searchedContactId, (newVal) => {
	nextTick(() => selectedValue.value = newVal?.value ?? null);
})
</script>

<style scoped lang="scss">
.project-page-panel-filters {
	display: flex;
	align-items: center;
	flex-direction: column;
	gap: 4px;
	width: 100%;
	background: #FFFFFF;
	border-top-left-radius: 8px;
	border-top-right-radius: 8px;
	border-bottom: 1px solid #F2F2F2;
	padding: 8px;

	&__top,
	&__bottom {
		display: flex;
		align-items: center;
		width: 100%;
	}

	&__address {
		width: 100%;
		box-sizing: border-box;
		padding-left: 22px;
	}

	&__use-union {
		display: flex;
		align-items: center;
		justify-content: center;
		margin-left: 8px;
	}

	&__search {
		width: 100%;
	}

	&__tags {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 4px;
		width: 100%;
		box-sizing: border-box;
		padding-left: 22px;

		&-item {
			display: flex;
			align-items: center;
			text-transform: lowercase;

			&-name {
				color: #979797;
				font-size: 14px;
				line-height: 18px;
				margin-right: 2px;
			}
		}
	}

	&::v-deep(.q-field__control) {
		height: 32px;
		min-height: 32px !important;
	}

	&::v-deep(.q-field__append) {
		height: 32px;
	}

	&::v-deep(.q-field__control-container) {
		padding-top: 4px !important;
	}
}
</style>
